import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Observable} from "rxjs";
import {
  CreateWorkspacePayload,
  DEFAULT_WORKSPACE_TONE,
  SingleWorkspaceResponse,
  Workspace,
  WorkspaceFile,
  WorkspaceResponse
} from "../models/workspace.model";
import {AuthService} from "./auth.service";
import {catchError, map, switchMap} from "rxjs/operators";
import {environment} from "../../environments/environment";

// Interface for file download API response
interface FileDownloadResponse {
  data: {
    file_id: number;
    filename: string;
    mime_type: string;
    file_size: number;
    content: string; // base64 encoded
  };
  metadata?: {
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {

  private apiUrl = environment.api.url;

  private token: string = '';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private generateInitials(name: string): string {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return words[0][0].toUpperCase() + words[1][0].toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  private transformWorkspace(workspace: Workspace): Workspace {
    return {
      ...workspace,
      // Legacy workspaces (pre lead-pipelines migration) omit `tone`; default to consultative.
      tone: workspace.tone ?? DEFAULT_WORKSPACE_TONE,
      initials: this.generateInitials(workspace.name),
      color: 'bg-violet-500'
    };
  }

  getAllWorkspaces(): Observable<{ workspaces: Workspace[] }> {
    const url = `${this.apiUrl}/workspaces`;

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.get<WorkspaceResponse>(url, httpOptions).pipe(
          map(response => {
            if (!response.data) {
              throw new Error('Failed to fetch workspaces: No data received');
            }

            // Transform workspaces to add UI properties
            const transformedWorkspaces = response.data.map(workspace =>
              this.transformWorkspace(workspace)
            );

            return {
              workspaces: transformedWorkspaces
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  getWorkspaceById(id: string, includeFiles: boolean = false): Observable<{ workspace: Workspace }> {
    const url = `${this.apiUrl}/workspaces/${id}`;
    
    // Always include ICP data
    const queryParams: string[] = ['include_icp=true'];
    if (includeFiles) {
      queryParams.push('include_files=true');
    }
    const params = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.get<SingleWorkspaceResponse>(`${url}${params}`, httpOptions).pipe(
          map(response => {
            if (!response.data) {
              throw new Error('Failed to fetch workspace: No data received');
            }

            const transformedWorkspace = this.transformWorkspace(response.data);

            return {
              workspace: transformedWorkspace
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  createWorkspace(payload: CreateWorkspacePayload): Observable<{ workspace: Workspace }> {
    const url = `${this.apiUrl}/workspaces`;

    // Add default values if not provided
    const requestPayload = {
      ...payload,
      is_default: payload.is_default ?? false,
      create_rag_collection: payload.create_rag_collection ?? true
    };

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.post<SingleWorkspaceResponse>(url, requestPayload, httpOptions).pipe(
          map(response => {
            if (!response.data) {
              throw new Error('Failed to create workspace: No data received');
            }

            const transformedWorkspace = this.transformWorkspace(response.data);

            return {
              workspace: transformedWorkspace
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  updateWorkspace(id: string, changes: Partial<CreateWorkspacePayload>): Observable<{ workspace: Workspace }> {
    const url = `${this.apiUrl}/workspaces/${id}`;

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.put<SingleWorkspaceResponse>(url, changes, httpOptions).pipe(
          map(response => {
            if (!response.data) {
              throw new Error('Failed to update workspace: No data received');
            }

            const transformedWorkspace = this.transformWorkspace(response.data);

            return {
              workspace: transformedWorkspace
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  deleteWorkspace(id: string): Observable<{ success: boolean }> {
    const url = `${this.apiUrl}/workspaces/${id}`;

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.delete<any>(url, httpOptions).pipe(
          map(response => {
            return {
              success: true
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  getWorkspaceFiles(workspaceId: string): Observable<{ files: WorkspaceFile[] }> {
    const url = `${this.apiUrl}/workspaces/${workspaceId}/files`;

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.get<{ data: WorkspaceFile[] }>(url, httpOptions).pipe(
          map(response => {
            if (!response.data) {
              throw new Error('Failed to fetch files: No data received');
            }

            return {
              files: response.data
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  // Upload a single file to workspace
  uploadFile(workspaceId: string, file: File, metadata?: any): Observable<{ success: boolean; data: any }> {
    const url = `${this.apiUrl}/workspaces/${workspaceId}/upload-file`;
    const formData = new FormData();

    formData.append('file', file, file.name);
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Authorization': `Bearer ${this.token}`
            // Don't set Content-Type for FormData - browser will set it with boundary
          })
        };

        return this.http.post<any>(url, formData, httpOptions).pipe(
          map(response => {
            return {
              success: true,
              data: response.data || response
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  // Delete file from workspace
  deleteFile(workspaceId: string, fileId: string): Observable<{ success: boolean }> {
    const url = `${this.apiUrl}/workspaces/${workspaceId}/files/${fileId}`;

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.delete<any>(url, httpOptions).pipe(
          map(response => {
            return {
              success: true
            };
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  downloadFileBlob(workspaceId: string, fileId: string): Observable<Blob> {
    const url = `${this.apiUrl}/workspaces/${workspaceId}/files/${fileId}/download`;

    return this.authService.token().pipe(
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        return this.http.get<FileDownloadResponse>(url, httpOptions).pipe(
          map(response => {
            if (!response.data || !response.data.content) {
              throw new Error('Failed to download file: No content received');
            }

            // Decode base64 content to binary
            const base64Content = response.data.content;
            const binaryString = atob(base64Content);
            const bytes = new Uint8Array(binaryString.length);

            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            // Create blob with proper MIME type
            const blob = new Blob([bytes], { type: response.data.mime_type || 'application/octet-stream' });

            return blob;
          }),
          catchError(error => { throw error; })
        );
      })
    );
  }

  getFileUrl(filePath: string): string {
    return `${this.apiUrl}/storage/${filePath}`;
  }

}
